import { useState, useEffect, useMemo } from "react";
import {
  Card,
  Metric,
  Text,
  Title,
  BarChart,
  LineChart,
} from "@tremor/react";
import DashboardSidebar from "./DashboardSidebar";
import DashboardHeader, { RestaurantIdentity } from "./DashboardHeader";
import { PALETTE } from "../lib/palette";
import { useFilterContext } from "../context/FilterContext";
import { apiFetch } from "../api/client";
import HeatmapRotacionMesas, { RotacionMesasData } from "./HeatmapRotacionMesas";
// import OcupabilidadSalon from "./OcupabilidadSalon"; // OCULTA: pendiente de afinar cálculos (spec 15.5)
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ── Tipos (espejo del backend /api/operaciones/*) ─────────────

interface Kpi {
  valor: number | null;
  delta_pct: number | null;
  unidad: string;
  fuente: string | null;
  detalle?: string | null;
}

interface ResumenResponse {
  restaurant_id: string;
  restaurant_name: string;
  anio: number;
  mm: number;
  mes_actual: string;
  tiempo_prom_servicio: Kpi;
  rotacion_mesas: Kpi;
  ticket_prom_mesa: Kpi;
  cobertura_turno: Kpi;
}

interface MeseroDesempeno {
  mesero: string;
  comensales: number;
  pedidos: number;
  cantidad: number;
  venta: number;
  ticket_promedio: number | null;
  consumo_comensal: number | null;
}

interface MeserosResponse {
  restaurant_id: string;
  restaurant_name: string;
  anio: number;
  mm: number;
  mes_actual: string;
  productividad_mesero: Kpi;
  ranking: MeseroDesempeno[];
}

interface TiempoFranja {
  franja: string;
  hora_inicio: number;
  minutos_prom: number | null;
  mesas_atendidas: number;
}

interface TiemposFranjaResponse {
  restaurant_id: string;
  anio: number;
  mm: number;
  mes_actual: string;
  franjas: TiempoFranja[];
}

interface RotacionMesasResponse {
  restaurant_id: string;
  anio: number;
  zonas: { zona: string; asientos: number }[];
  meses: {
    mm: number;
    label: string;
    comensales: number | null;
    mesas_disponibles: number;
    rotacion: number | null;
  }[];
}

interface PuntoTendenciaMesero {
  mm: number;
  label: string;
  ventas_netas: number;
  n_meseros: number;
  venta_por_mesero: number | null;
}

interface TicketMesaDow {
  dia: string;
  ventas_netas: number;
  n_mesas_atendidas: number;
  ticket_prom: number | null;
}

interface GraficasMeseroMesaResponse {
  restaurant_id: string;
  anio: number;
  mm_hasta: number;
  mes_actual: string;
  tendencia_mesero: PuntoTendenciaMesero[];
  ticket_mesa_dow: TicketMesaDow[];
}

interface RotacionCategoriaItem {
  categoria: string;
  grupo: string;
  serie: (number | null)[];
}

interface RotacionInventarioResponse {
  restaurant_id: string;
  anio: number;
  mm: number;
  mes_actual: string;
  meses: string[];
  categorias: RotacionCategoriaItem[];
}

// ── Helpers ──────────────────────────────────────────────────

const formatCurrency = (n: number) =>
  new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN", maximumFractionDigits: 0 }).format(n);

const formatNumber = (n: number, dec = 1) =>
  new Intl.NumberFormat("es-PE", { minimumFractionDigits: 0, maximumFractionDigits: dec }).format(n);

/** Convierte minutos totales a "MM:SS" (estilo del mock: "18:45 min"). */
function formatMinutos(min: number): string {
  const totalSec = Math.round(min * 60);
  const mm = Math.floor(totalSec / 60);
  const ss = String(totalSec % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/** Renderiza un valor KPI; si es null muestra "Sin datos". */
function renderKpi(
  value: number | null | undefined,
  formatter: (n: number) => string
): string {
  if (value == null) return "Sin datos";
  return formatter(value);
}

// ── Componente principal ─────────────────────────────────────

export default function OperationsDashboard() {
  const {
    restaurants, meses, selectedRestaurantId, selectedRestaurantSlug,
    mesSel, sidebarOpen, error, setSelectedRestaurant, setMesSel, toggleSidebar,
  } = useFilterContext();

  const [resumen, setResumen] = useState<ResumenResponse | null>(null);
  const [meseros, setMeseros] = useState<MeserosResponse | null>(null);
  const [tiemposFranja, setTiemposFranja] = useState<TiemposFranjaResponse | null>(null);
  const [rotacionMesas, setRotacionMesas] = useState<RotacionMesasData | null>(null);
  const [graficasMeseroMesa, setGraficasMeseroMesa] = useState<GraficasMeseroMesaResponse | null>(null);
  const [rotacionInventario, setRotacionInventario] = useState<RotacionInventarioResponse | null>(null);

  // ── Fetch: resumen de KPIs ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setResumen(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/operaciones/resumen?${params}`);
        if (!res.ok) {
          if (!cancelled) setResumen(null);
          return;
        }
        const body: ResumenResponse = await res.json();
        if (!cancelled) setResumen(body);
      } catch (err) {
        console.warn("Error fetching resumen:", err);
        if (!cancelled) setResumen(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: KPI 17/18 (productividad y productos por mesero) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setMeseros(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/operaciones/meseros?${params}`);
        if (!res.ok) {
          if (!cancelled) setMeseros(null);
          return;
        }
        const body: MeserosResponse = await res.json();
        if (!cancelled) setMeseros(body);
      } catch (err) {
        console.warn("Error fetching meseros:", err);
        if (!cancelled) setMeseros(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: tiempo prom. servicio por franja horaria ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setTiemposFranja(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/operaciones/tiempos-servicio-franja?${params}`);
        if (!res.ok) {
          if (!cancelled) setTiemposFranja(null);
          return;
        }
        const body: TiemposFranjaResponse = await res.json();
        if (!cancelled) setTiemposFranja(body);
      } catch (err) {
        console.warn("Error fetching tiempos-servicio-franja:", err);
        if (!cancelled) setTiemposFranja(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  // ── Fetch: gráficas de mesero y ticket por mesa (lo corrido del año) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setGraficasMeseroMesa(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/operaciones/graficas-mesero-mesa?${params}`);
        if (!res.ok) {
          if (!cancelled) setGraficasMeseroMesa(null);
          return;
        }
        const body: GraficasMeseroMesaResponse = await res.json();
        if (!cancelled) setGraficasMeseroMesa(body);
      } catch (err) {
        console.warn("Error fetching graficas-mesero-mesa:", err);
        if (!cancelled) setGraficasMeseroMesa(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);
  // ── Fetch: rotación de inventario por tipo de producto (categoría food cost) ──
  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setRotacionInventario(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
          mm: String(mesSel.mm),
        });
        const res = await apiFetch(`/api/operaciones/rotacion-inventario?${params}`);
        if (!res.ok) {
          if (!cancelled) setRotacionInventario(null);
          return;
        }
        const body: RotacionInventarioResponse = await res.json();
        if (!cancelled) setRotacionInventario(body);
      } catch (err) {
        console.warn("Error fetching rotacion-inventario:", err);
        if (!cancelled) setRotacionInventario(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  useEffect(() => {
    if (!selectedRestaurantSlug || !mesSel) {
      setRotacionMesas(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          restaurant_id: selectedRestaurantSlug,
          anio: String(mesSel.anio),
        });
        const res = await apiFetch(`/api/operaciones/rotacion-mesas?${params}`);
        if (!res.ok) {
          if (!cancelled) setRotacionMesas(null);
          return;
        }
        const body: RotacionMesasResponse = await res.json();
        if (!cancelled) setRotacionMesas(body as RotacionMesasData);
      } catch (err) {
        console.warn("Error fetching rotacion-mesas:", err);
        if (!cancelled) setRotacionMesas(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRestaurantSlug, mesSel]);

  const selectedRestaurant = useMemo(
    () => restaurants.find((r) => r.id === selectedRestaurantId) ?? null,
    [restaurants, selectedRestaurantId]
  );

  return (
    <div className="flex gap-3">
      {/* ── Sidebar compartida (template homologado) ── */}
      <DashboardSidebar
        restaurants={restaurants as RestaurantIdentity[]}
        selectedRestaurantId={selectedRestaurantId}
        onRestaurantChange={setSelectedRestaurant}
        months={meses}
        selectedMonth={mesSel}
        onMonthChange={setMesSel}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={toggleSidebar}
      />

      {/* ── Contenido principal ── */}
      <div className="flex-1 min-w-0 space-y-3">
        {/* Header del dashboard: icono + 3 líneas (tablero / restaurante / descripción-periodo) */}
        {selectedRestaurant ? (
          <DashboardHeader
            title="Operaciones & RRHH"
            description="Visión integral de eficiencia operativa y gestión del talento"
            restaurant={selectedRestaurant as RestaurantIdentity}
            period={resumen?.mes_actual}
          />
        ) : (
          <Title>Operaciones &amp; RRHH</Title>
        )}

        {error && (
          <Card className="!p-3">
            <Text className="text-red-600">Error cargando restaurants: {error.message}</Text>
          </Card>
        )}

        {/* Primera fila: KPIs operativos (izq) + RRHH KPI 17 / Ranking (der) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          {/* Columna izquierda: 4 KPIs operativos */}
          <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="!p-3">
              <Text className="text-xs">⏱️ Tiempo prom. servicio</Text>
              <Metric className="!text-xl">
                {resumen?.tiempo_prom_servicio.valor != null
                  ? `${formatMinutos(resumen.tiempo_prom_servicio.valor)} min`
                  : "Sin datos"}
              </Metric>
              <Text className="!text-[10px] mt-1 text-tremor-content">registro → pago</Text>
            </Card>

            <Card className="!p-3">
              <Text className="text-xs">🔄 Rotación de mesas</Text>
              <Metric className="!text-xl">
                {renderKpi(resumen?.rotacion_mesas.valor, (n) => `${formatNumber(n, 1)} veces`)}
              </Metric>
              <Text className="!text-[10px] mt-1 text-tremor-content">Σ rotaciones ÷ nº mesas (Rotación por Mesas)</Text>
            </Card>

            <Card className="!p-3">
              <Text className="text-xs">💳 Ticket prom. por mesa</Text>
              <Metric className="!text-xl">
                {renderKpi(resumen?.ticket_prom_mesa.valor, formatCurrency)}
              </Metric>
              <Text className="!text-[10px] mt-1 text-tremor-content">venta ÷ mesas atendidas</Text>
            </Card>

            <Card className="!p-3">
              <Text className="text-xs">📊 Cobertura por turno</Text>
              <Metric className="!text-xl">
                {renderKpi(resumen?.cobertura_turno.valor, (n) => `${formatNumber(n, 1)}%`)}
              </Metric>
              <Text className="!text-[10px] mt-1 text-tremor-content">
                {resumen?.cobertura_turno.detalle ?? 'mesas ocupadas ÷ disponibles'}
              </Text>
            </Card>
          </div>

          {/* Columna derecha: RRHH — KPI 17 (productividad) + Ranking de meseros */}
          {meseros && meseros.ranking.length > 0 ? (
            <div className="lg:col-span-1 flex flex-col gap-3">
              <Card className="!p-4">
                <Text className="text-xs text-tremor-content">🧑‍🍳 Productividad por mesero (KPI 17)</Text>
                <Metric className="!text-xl">
                  {meseros.productividad_mesero.valor != null
                    ? `${formatCurrency(meseros.productividad_mesero.valor)} / mesero`
                    : "Sin datos"}
                </Metric>
                <Text className="!text-[10px] mt-1 text-tremor-content">
                  {meseros.productividad_mesero.detalle ?? "venta ÷ nº meseros"}
                </Text>
              </Card>
            </div>
          ) : (
            <div className="lg:col-span-1">
              <Card className="!p-4">
                <Text className="text-xs text-tremor-content">🧑‍🍳 Productividad por mesero (KPI 17)</Text>
                <Metric className="!text-xl">Sin datos</Metric>
                <Text className="!text-[10px] mt-1 text-tremor-content">
                  Sube el reporte &quot;Analítico de Productos por Meseros&quot; en Admin para activar la productividad.
                </Text>
              </Card>
            </div>
          )}
        </div>

        {/* Segunda fila: Ranking de meseros (izq 50%) + Tiempo prom. servicio por franja (der 50%) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Ranking de meseros (izquierda) */}
          {meseros && meseros.ranking.length > 0 ? (
            <Card className="!p-4">
              <Title className="!text-sm mb-2">Ranking de meseros</Title>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-tremor-content border-b border-tremor-border">
                      <th className="py-1 pr-2">Mesero</th>
                      <th className="py-1 px-2 text-right">Comensales</th>
                      <th className="py-1 px-2 text-right">Pedidos</th>
                      <th className="py-1 px-2 text-right">Venta</th>
                      <th className="py-1 px-2 text-right">Ticket prom.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meseros.ranking.map((m) => (
                      <tr key={m.mesero} className="border-b border-tremor-border/50">
                        <td className="py-1 pr-2 font-medium">{m.mesero}</td>
                        <td className="py-1 px-2 text-right">{formatNumber(m.comensales, 0)}</td>
                        <td className="py-1 px-2 text-right">{formatNumber(m.pedidos, 0)}</td>
                        <td className="py-1 px-2 text-right">{formatCurrency(m.venta)}</td>
                        <td className="py-1 px-2 text-right">
                          {m.ticket_promedio != null ? formatCurrency(m.ticket_promedio) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <Card className="!p-4">
              <Title className="!text-sm mb-2">Ranking de meseros</Title>
              <Text className="!text-xs text-tremor-content">
                Sube el reporte &quot;Analítico de Productos por Meseros&quot; en Admin para activar el ranking.
              </Text>
            </Card>
          )}

          {/* Tiempo promedio de servicio por franja horaria (derecha) */}
          {tiemposFranja && tiemposFranja.franjas.some((f) => f.minutos_prom != null) ? (
            <Card className="!p-3">
              <Title className="!text-base">Tiempo promedio de servicio por franja horaria</Title>
              <Text className="!text-xs text-tremor-content">
                Σ(Hora pago − Hora ingreso) ÷ N° de mesas atendidas · minutos · {tiemposFranja.mes_actual}
              </Text>
              <BarChart
                className="mt-3 h-64"
                data={tiemposFranja.franjas.map((f) => ({
                  franja: f.franja,
                  "Minutos (servicio)": f.minutos_prom ?? 0,
                }))}
                index="franja"
                categories={["Minutos (servicio)"]}
                colors={["#15335B"]}
                valueFormatter={(n) => `${n} min`}
                yAxisWidth={48}
                showLegend={false}
              />
            </Card>
          ) : (
            <Card className="!p-3">
              <Title className="!text-base">Tiempo promedio de servicio por franja horaria</Title>
              <Text className="!text-xs text-tremor-content">
                Sin datos de tiempos en salón para este periodo. Sube el reporte de
                "Admin → Tiempos en salón" para activar esta gráfica.
              </Text>
            </Card>
          )}
        </div>

        {/* Mapa de calor: rotación de mesas por zona × mes */}
        {rotacionMesas && rotacionMesas.zonas.length > 0 ? (
          <HeatmapRotacionMesas data={rotacionMesas} />
        ) : (
          <Card className="!p-3">
            <Title className="!text-base">Rotación de mesas</Title>
            <Text className="!text-xs text-tremor-content">
              Sin inventario de mesas cargado. Sube el reporte "Rotación por Mesas"
              en Admin para activar esta gráfica.
            </Text>
          </Card>
        )}

        {/* Gráfica: tendencia de ventas por mesero (línea) + ticket por mesa por día (barras) */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
          {/* Ventas por mesero (barras) + n° meseros (línea, eje secundario) */}
          <Card className='!p-3'>
            <Title className='!text-base'>🧑‍🍳 Ventas por mesero</Title>
            <Text className='!text-xs text-tremor-content'>
              {graficasMeseroMesa?.mes_actual}
            </Text>
            {graficasMeseroMesa && graficasMeseroMesa.tendencia_mesero.length > 0 ? (
              <ResponsiveContainer width='100%' height={256}>
                <ComposedChart data={graficasMeseroMesa.tendencia_mesero.map((p) => ({
                  mes: p.label,
                  'Venta por mesero (S/)': p.venta_por_mesero ?? 0,
                  'N° meseros': p.n_meseros,
                }))}>
                  <CartesianGrid vertical={false} stroke='#e5e7eb' />
                  <XAxis
                    dataKey='mes'
                    tick={{ fontSize: 12, fill: '#687385' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    yAxisId='left'
                    tickFormatter={(v) => formatCurrency(v)}
                    width={70}
                    tick={{ fontSize: 12, fill: '#687385' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId='right'
                    orientation='right'
                    allowDecimals={false}
                    width={36}
                    tick={{ fontSize: 12, fill: '#687385' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === 'N° meseros' ? `${value}` : formatCurrency(value)
                    }
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 8,
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: '#687385' }}
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='Venta por mesero (S/)'
                    fill={PALETTE.primaryDark}
                    barSize={28}
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    yAxisId='right'
                    type='monotone'
                    dataKey='N° meseros'
                    stroke={PALETTE.orange}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Text className='!text-xs text-tremor-content mt-3'>Sin datos de meseros para este periodo.</Text>
            )}
          </Card>

          {/* Ticket promedio por mesa por día de semana (barras) */}
          <Card className='!p-3'>
            <Title className='!text-base'>💳 Ticket prom. por mesa por día</Title>
            <Text className='!text-xs text-tremor-content'>
              {graficasMeseroMesa?.mes_actual}
            </Text>
            {graficasMeseroMesa && graficasMeseroMesa.ticket_mesa_dow.some((d) => d.ticket_prom != null) ? (
              <BarChart
                className='mt-3 h-64'
                data={graficasMeseroMesa.ticket_mesa_dow.map((d) => ({
                  dia: d.dia,
                  'Ticket (S/)': d.ticket_prom ?? 0,
                }))}
                index='dia'
                categories={['Ticket (S/)']}
                colors={[PALETTE.primaryDark]}
                valueFormatter={formatCurrency}
                yAxisWidth={70}
                showLegend={false}
              />
            ) : (
              <Text className='!text-xs text-tremor-content mt-3'>Sin datos de mesas atendidas para este periodo.</Text>
            )}
          </Card>
        </div>

        {/* Rotación de inventario por tipo de producto (categoría de food cost) */}
        <Card className='!p-3'>
          <Title className='!text-base'>📦 Rotación de inventario por tipo de producto</Title>
          <Text className='!text-xs text-tremor-content'>
            {rotacionInventario?.mes_actual} · ((inv. inicial + inv. final) / 2) ÷ CMV por mes
          </Text>
          {rotacionInventario && rotacionInventario.categorias.length > 0 ? (
            <LineChart
              className='mt-3 h-96'
              data={rotacionInventario.meses.map((mesLabel, i) => {
                const row: Record<string, number | string> = { mes: mesLabel };
                for (const c of rotacionInventario!.categorias) {
                  const v = c.serie[i];
                  if (v != null) row[c.categoria] = v;
                }
                return row;
              })}
              index='mes'
              categories={rotacionInventario.categorias.map((c) => c.categoria)}
              colors={rotacionInventario.categorias.map(
                (_, i) => ['blue', 'orange', 'violet', 'emerald', 'rose', 'cyan',
                           'amber', 'indigo', 'fuchsia', 'teal', 'lime', 'sky',
                           'pink', 'purple', 'blue', 'orange', 'violet'][i % 17]
              )}
              valueFormatter={(n) => `${n} veces`}
              yAxisWidth={48}
              showLegend={true}
              showAnimation={false}
            />
          ) : (
            <Text className='!text-xs text-tremor-content mt-3'>Sin datos de food cost para este periodo.</Text>
          )}
        </Card>

        {/* Ocupabilidad de salón por franja horaria — OCULTA (pendiente de terminar
            y afinar cálculos; ver spec 15.5). Se retoma cuando se definan las
            fórmulas definitivas. */}

        {/* Nota de alcance */}
        <Card className="!p-3">
          <Text className="!text-xs text-tremor-content">
            KPIs activos: Tiempo prom. servicio (oper_tiempos_salon, con desglose por
            franja horaria), Ticket prom. por mesa y Cobertura por turno
            (oper_ocupabilidad_mesas), Rotación de mesas (pendiente de carga).
            Gráficas nuevas: Ventas por mesero (línea, ene→mes filtrado,
            oper_productos_mesero) y Ticket prom. por mesa por día de semana
            (barras: comercial_diario_ventas ÷ mesas de oper_tiempos_salon).
            Ventas totales, Food cost, Merma y % Costo laboral fueron retirados del
            tablero por indicación del usuario; la tabla KPI 18 (productos por mesero)
            también se excluyó.
          </Text>
        </Card>
      </div>
    </div>
  );
}
